import * as React from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { 
  Elements, 
  CardElement, 
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js'
import { Button } from '../../components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/dialog'
import { Card, CardContent } from '../../components/card'
import { showToast } from '../../components/toast'

// Custom CSS to override Stripe Elements styling and ensure proper theme colors
const stripeElementCSS = `
  .StripeElement {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
  }
  
  .StripeElement--webkit-autofill {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
  }
  
  /* Base text styling for all states */
  .StripeElement input,
  .StripeElement--empty input,
  .StripeElement--complete input,
  .StripeElement--invalid input {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
    transition: none !important;
  }
  
  /* Comprehensive browser autofill override - all possible selectors */
  .StripeElement input:-webkit-autofill,
  .StripeElement input:-webkit-autofill:hover,
  .StripeElement input:-webkit-autofill:focus,
  .StripeElement input:-webkit-autofill:active,
  .StripeElement input[data-stripe="input"]:-webkit-autofill,
  .StripeElement input[autocomplete]:-webkit-autofill,
  .StripeElement--autofill input,
  .StripeElement input[aria-invalid="false"]:-webkit-autofill,
  .StripeElement input[aria-invalid="true"]:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    caret-color: hsl(var(--foreground)) !important;
    box-shadow: 0 0 0 1000px transparent inset !important;
    transition: none !important;
  }
  
  /* Firefox autofill support */
  .StripeElement input:-moz-autofill,
  .StripeElement input:-moz-autofill-preview {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    box-shadow: none !important;
  }
  
  /* Dark mode specific overrides */
  .dark .StripeElement,
  .dark .StripeElement input,
  .dark .StripeElement--empty input,
  .dark .StripeElement--complete input,
  .dark .StripeElement--invalid input {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
  }
  
  .dark .StripeElement input:-webkit-autofill,
  .dark .StripeElement input:-webkit-autofill:hover,
  .dark .StripeElement input:-webkit-autofill:focus,
  .dark .StripeElement input:-webkit-autofill:active,
  .dark .StripeElement input[data-stripe="input"]:-webkit-autofill,
  .dark .StripeElement input[autocomplete]:-webkit-autofill,
  .dark .StripeElement--autofill input {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    caret-color: hsl(var(--foreground)) !important;
    box-shadow: 0 0 0 1000px transparent inset !important;
  }
  
  /* Light mode specific overrides */
  :not(.dark) .StripeElement,
  :not(.dark) .StripeElement input,
  :not(.dark) .StripeElement--empty input,
  :not(.dark) .StripeElement--complete input,
  :not(.dark) .StripeElement--invalid input {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
  }
  
  :not(.dark) .StripeElement input:-webkit-autofill,
  :not(.dark) .StripeElement input:-webkit-autofill:hover,
  :not(.dark) .StripeElement input:-webkit-autofill:focus,
  :not(.dark) .StripeElement input:-webkit-autofill:active,
  :not(.dark) .StripeElement input[data-stripe="input"]:-webkit-autofill,
  :not(.dark) .StripeElement input[autocomplete]:-webkit-autofill,
  :not(.dark) .StripeElement--autofill input {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: hsl(var(--foreground)) !important;
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
    caret-color: hsl(var(--foreground)) !important;
    box-shadow: 0 0 0 1000px transparent inset !important;
  }
`

// Initialize Stripe with publishable key (this will be set from environment)
const stripePromise = loadStripe(
  (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_...' // Replace with actual key
)

interface AddPaymentMethodDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  patientId: string
}

interface AddPaymentMethodFormProps {
  onSuccess: () => void
  patientId: string
  onClose: () => void
}

const AddPaymentMethodForm = ({ onSuccess, patientId, onClose }: AddPaymentMethodFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Inject CSS to override Stripe autofill styling and get computed colors
  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = stripeElementCSS
    document.head.appendChild(style)
    
    // Get the computed theme colors and inject dynamic CSS
    const themeColors = getThemeColors()
    
    // Add comprehensive autofill override CSS with computed colors
    const dynamicAutofillCSS = `
      .StripeElement input {
        color: ${themeColors.foreground} !important;
        -webkit-text-fill-color: ${themeColors.foreground} !important;
      }
      
      .StripeElement input::placeholder {
        color: ${themeColors.mutedForeground} !important;
        opacity: 0.7 !important;
      }
      
      /* Use computed theme colors for autofill - no hardcoded values */
      
      /* Comprehensive autofill text color override with computed colors as fallback */
      .StripeElement input:-webkit-autofill,
      .StripeElement input:-webkit-autofill:hover,
      .StripeElement input:-webkit-autofill:focus,
      .StripeElement input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
        -webkit-text-fill-color: ${themeColors.foreground} !important;
        background-color: transparent !important;
        color: ${themeColors.foreground} !important;
        caret-color: ${themeColors.foreground} !important;
        box-shadow: 0 0 0 1000px transparent inset !important;
        transition: none !important;
      }
      
      /* Firefox autofill support */
      .StripeElement input:-moz-autofill {
        background-color: transparent !important;
        color: ${themeColors.foreground} !important;
        box-shadow: none !important;
      }
      
      /* Force text color in all autofill states */
      .StripeElement input[data-stripe="input"] {
        -webkit-text-fill-color: ${themeColors.foreground} !important;
        color: ${themeColors.foreground} !important;
      }
      
      /* Force rerender class for nuclear option */
      .StripeElement.force-rerender * {
        animation: none !important;
        transform: translateZ(0) !important;
      }
    `
    
    const dynamicStyle = document.createElement('style')
    dynamicStyle.textContent = dynamicAutofillCSS
    document.head.appendChild(dynamicStyle)
    
    // Aggressive autofill detection and fixing
    let mutationObserver: MutationObserver | null = null
    
    const fixAutofillColors = () => {
      // Use the computed theme colors - no hardcoded values
      const forceColor = themeColors.foreground
      
      // Target all possible Stripe input selectors
      const selectors = [
        '.StripeElement input',
        '.StripeElement input[data-stripe="input"]',
        '.StripeElement input[autocomplete]',
        '.StripeElement input[aria-invalid]',
        '.StripeElement--complete input',
        '.StripeElement--invalid input',
        '.StripeElement--empty input'
      ]
      
      selectors.forEach(selector => {
        const inputs = document.querySelectorAll(selector)
        inputs.forEach((input: any) => {
          if (input) {
            // Nuclear approach: Force override with hardcoded colors
            input.style.setProperty('-webkit-text-fill-color', forceColor, 'important')
            input.style.setProperty('color', forceColor, 'important')
            input.style.setProperty('background-color', 'transparent', 'important')
            input.style.setProperty('box-shadow', '0 0 0 1000px transparent inset', 'important')
            input.style.setProperty('-webkit-box-shadow', '0 0 0 1000px transparent inset', 'important')
            input.style.setProperty('caret-color', forceColor, 'important')
            input.style.setProperty('transition', 'none', 'important')
            
            // Also try computed color as fallback
            input.style.setProperty('--webkit-text-fill-color-fallback', themeColors.foreground, 'important')
            
            // Force focus to trigger re-calculation
            if (input === document.activeElement) {
              input.blur()
              setTimeout(() => input.focus(), 10)
            }
            
            // Also set as data attribute for persistence
            input.setAttribute('data-theme-color', forceColor)
            
            // Debug logging to verify colors
            console.log('🎨 AUTOFILL COLOR FIX:', {
              element: input,
              forceColor: forceColor,
              computedColor: themeColors.foreground,
              isDarkMode: document.documentElement.classList.contains('dark')
            })
          }
        })
      })
    }
    
    // Initial fix
    setTimeout(fixAutofillColors, 100)
    
    // Set up aggressive autofill detection with re-render trigger
    const setupAutofillListeners = () => {
      const stripeElement = document.querySelector('.StripeElement')
      if (stripeElement) {
        // Listen for input events that indicate autofill
        const events = ['input', 'change', 'blur', 'focus', 'animationstart']
        events.forEach(eventType => {
          stripeElement.addEventListener(eventType, fixAutofillColors, true)
        })
        
        // Nuclear option: Force re-render on autofill detection
        const forceRerender = () => {
          // Add a CSS class that forces recalculation
          stripeElement.classList.add('force-rerender')
          setTimeout(() => {
            stripeElement.classList.remove('force-rerender')
            fixAutofillColors()
          }, 10)
        }
        
        // Special handling for webkit autofill animation
        stripeElement.addEventListener('animationstart', (e: any) => {
          if (e.animationName === 'autofill') {
            setTimeout(() => {
              fixAutofillColors()
              forceRerender()
            }, 10)
          }
        })
        
        // Detect autofill state change
        const inputs = stripeElement.querySelectorAll('input')
        inputs.forEach((input: any) => {
          // Monitor for autofill pseudo-class changes
          const checkAutofill = () => {
            if (input.matches && input.matches(':-webkit-autofill')) {
              fixAutofillColors()
              forceRerender()
            }
          }
          
          input.addEventListener('input', checkAutofill)
          input.addEventListener('change', checkAutofill)
        })
      }
    }
    
    // Set up mutation observer to catch dynamic changes
    mutationObserver = new MutationObserver(() => {
      fixAutofillColors()
    })
    
    // Start observing for autofill changes
    setTimeout(() => {
      const stripeElement = document.querySelector('.StripeElement')
      if (stripeElement) {
        mutationObserver?.observe(stripeElement, { 
          childList: true, 
          subtree: true, 
          attributes: true,
          attributeFilter: ['style', 'class', 'aria-invalid', 'data-stripe']
        })
        
        setupAutofillListeners()
      }
    }, 500)
    
    // Periodic fix for persistent autofill issues
    const intervalId = setInterval(fixAutofillColors, 1000)
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
      if (document.head.contains(dynamicStyle)) {
        document.head.removeChild(dynamicStyle)
      }
      if (mutationObserver) {
        mutationObserver.disconnect()
      }
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements) {
      console.log('Stripe not loaded yet')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log('🔍 Creating setup intent for patient:', patientId)
      
      // Create setup intent
      const module = await import('@joinomu/shared')
      const setupIntentResult = await module.StripeService.createSetupIntent(patientId)
      
      if (!setupIntentResult.success) {
        throw new Error(setupIntentResult.error || 'Failed to create setup intent')
      }

      console.log('✅ Setup intent created successfully')

      // Confirm setup intent with card
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      console.log('🔍 Confirming setup intent with card details...')
      
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupIntentResult.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: 'Patient' // You could get this from user profile
            }
          }
        }
      )

      if (stripeError) {
        console.error('Setup failed:', stripeError)
        throw new Error(stripeError.message || 'Failed to add payment method')
      }

      console.log('✅ Payment method added successfully:', setupIntent?.id)
      
      showToast({
        title: 'Payment Method Added',
        description: 'Your new payment method has been added successfully.',
        variant: 'success'
      })

      onSuccess()
      onClose()
    } catch (error) {
      console.error('❌ Error adding payment method:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to add payment method'
      setError(errorMessage)
      
      showToast({
        title: 'Failed to Add Payment Method',
        description: errorMessage,
        variant: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Get computed theme colors for Stripe Elements
  const getThemeColors = React.useCallback(() => {
    if (typeof window === 'undefined') return {
      foreground: '#000000',
      mutedForeground: '#6b7280',
      destructive: '#ef4444'
    }
    
    const root = document.documentElement
    const computedStyle = getComputedStyle(root)
    
    // Get the raw CSS custom property values
    const foregroundValue = computedStyle.getPropertyValue('--foreground').trim()
    const mutedForegroundValue = computedStyle.getPropertyValue('--muted-foreground').trim()
    const destructiveValue = computedStyle.getPropertyValue('--destructive').trim()
    
    // Convert oklch values to RGB values that Stripe can understand
    const convertToColor = (cssValue: string) => {
      if (!cssValue) return '#000000'
      
      try {
        // Create a temporary element to compute the actual color
        const testEl = document.createElement('div')
        testEl.style.position = 'absolute'
        testEl.style.visibility = 'hidden'
        testEl.style.color = `oklch(${cssValue})`
        document.body.appendChild(testEl)
        
        // Get the computed RGB color
        const computedColor = getComputedStyle(testEl).color
        document.body.removeChild(testEl)
        
        // Convert rgb() to hex if needed
        if (computedColor.startsWith('rgb')) {
          const match = computedColor.match(/\d+/g)
          if (match && match.length >= 3) {
            const r = parseInt(match[0]).toString(16).padStart(2, '0')
            const g = parseInt(match[1]).toString(16).padStart(2, '0')
            const b = parseInt(match[2]).toString(16).padStart(2, '0')
            return `#${r}${g}${b}`
          }
        }
        
        return computedColor || '#000000'
      } catch (error) {
        console.warn('Color conversion failed:', error)
        return '#000000'
      }
    }
    
    return {
      foreground: convertToColor(foregroundValue),
      mutedForeground: convertToColor(mutedForegroundValue),
      destructive: convertToColor(destructiveValue)
    }
  }, [])

  const themeColors = getThemeColors()

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: themeColors.foreground,
        backgroundColor: 'transparent',
        lineHeight: '1.5',
        '::placeholder': {
          color: themeColors.mutedForeground,
        },
      },
      invalid: {
        color: themeColors.destructive,
        iconColor: themeColors.destructive,
      },
      complete: {
        color: themeColors.foreground,
        iconColor: themeColors.foreground,
      },
    },
    hidePostalCode: false,
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      
      <div className="space-y-2">
        <label className="text-sm font-medium">Card Information</label>
        <Card>
          <CardContent className="p-3">
            <CardElement 
              options={cardElementOptions}
              onChange={(event) => {
                if (event.error) {
                  setError(event.error.message)
                } else {
                  setError(null)
                }
              }}
            />
          </CardContent>
        </Card>
        <p className="text-xs text-muted-foreground">
          Your card information is encrypted and secure. We use Stripe for secure payment processing.
        </p>
      </div>
      
      <div className="flex space-x-3 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          disabled={isLoading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={!stripe || isLoading} 
          className="flex-1"
        >
          {isLoading ? 'Adding...' : 'Add Payment Method'}
        </Button>
      </div>
    </form>
  )
}

export const AddPaymentMethodDialog = ({ 
  open, 
  onOpenChange, 
  onSuccess, 
  patientId 
}: AddPaymentMethodDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Payment Method</DialogTitle>
        </DialogHeader>
        <Elements stripe={stripePromise}>
          <AddPaymentMethodForm 
            onSuccess={onSuccess} 
            patientId={patientId}
            onClose={() => onOpenChange(false)}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  )
}

export type { AddPaymentMethodDialogProps }